import React, {FC, useEffect, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {useAuth} from '../../auth'
import {useCertificateDeleteToast} from '../core/useCertificateDeleteToast'
import { QHSE_PERMS } from '../core/permissions'
import {OfficeCertificateUploadModal} from './OfficeCertificateUploadModal'
import {CertificateViewer} from './CertificateViewer'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import {Vessel} from '../../Management/core/_models'
import {OfficeCertificateEditModal} from './OfficeCertificateEditModal'
import {OfficeCertificateRevisionsModal} from './OfficeCertificateRevisionsModal'
import {CertificateLibraryModal} from './CertificateLibraryModal'
import {
  updateOfficeCertificate,
  replaceOfficeCertificateFile,
  officeCertificateViewUrl,
  officeCertificateDownloadUrl,
} from '../core/_requests'
import { isLikelyVesselAssignedUser } from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { RemarksCell, RemarksModal } from './StructuredRemarksView'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'

interface Certificate {
  id: string
  vesselId?: number
  certificateName: string
  certificateNameId?: number
  certificateNameText?: string
  certificateCategoryId?: number
  certificateCategoryName?: string
  dateOfIssue?: string
  dateOfExpiry?: string
  uploadedDate: string
  uploadedBy: string
  uploadedByName: string
  fileName: string
  fileUrl: string
  fileSize: string
  remarks?: string
  revisionCount?: number
  currentFileRevisionId?: number | null
  fileMime?: string
  filePath?: string
}

type SortConfig = {
  key: 'certificateName' | 'dateOfIssue' | 'dateOfExpiry' | 'uploadedByName' | 'remarks' | 'revisionCount' | ''
  direction: 'asc' | 'desc'
}

const API_URL = process.env.REACT_APP_API_URL
const OFFICE_CERTIFICATE_API_URL = `${API_URL}/qhse/office-certificates`

const getExpiryStatus = (expiryDate?: string) => {
  if (!expiryDate) {
    return {status: 'no-expiry' as const, days: null as number | null, color: 'secondary'}
  }
  const today = new Date()
  const expiry = new Date(expiryDate)
  const diffTime = expiry.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 0) return {status: 'expired' as const, days: Math.abs(diffDays), color: 'danger'}
  if (diffDays <= 30) return {status: 'expiring' as const, days: diffDays, color: 'warning'}
  return {status: 'valid' as const, days: diffDays, color: 'success'}
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const OfficeCertificates: FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const confirmDelete = useCertificateDeleteToast()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const [allVessels, setAllVessels] = useState<Vessel[]>([])
  const [isLoadingVessels, setIsLoadingVessels] = useState(true)

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  const {currentUser, auth, can, permissionsLoaded, isSuperAdmin} = useAuth()

  const [loading, setLoading] = useState(false)
  const isDataLoading = loading || isLoadingVessels
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const canCreateCert = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.OFFICE_CERT_CREATE))
  const canUpdateCert = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.OFFICE_CERT_UPDATE))
  const canDeleteCert = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.OFFICE_CERT_DELETE))
  const canDownloadCert = isSuperAdmin || (permissionsLoaded && can(QHSE_PERMS.OFFICE_CERT_DOWNLOAD))
  const showCertificateLibrary =
    isSuperAdmin ||
    (permissionsLoaded &&
      (can(QHSE_PERMS.CERT_NAME_READ) || can(QHSE_PERMS.CERT_CATEGORY_READ)))

  const selectedVesselId = String(
    (shipboardUser ? currentUser?.vessel?.id : globalState.vessel?.id) ?? ''
  )

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editingCert, setEditingCert] = useState<Certificate | null>(null)
  const [isLibraryOpen, setIsLibraryOpen] = useState(false)
  const [revModal, setRevModal] = useState<{id:number, name:string, visible:boolean}>({id:0, name:'', visible:false})
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })
  const [reloadTick, setReloadTick] = useState(0)
  const forceReload = () => setReloadTick(t => t + 1)

  // Match the global company filter while keeping the accessible-vessel scope.
  const vessels = React.useMemo(() => {
    if (!globalState.company) return allVessels
    const companyVesselIds = new Set(globalVessels.map(vessel => String(vessel.id)))
    return allVessels.filter(vessel => companyVesselIds.has(String(vessel.id)))
  }, [allVessels, globalState.company, globalVessels])

  // Sync global search query
  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchQuery(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  // Fetch vessels accessible to current user
  useEffect(() => {
    const fetchVessels = async () => {
      setIsLoadingVessels(true)
      try {
        const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        setAllVessels(vesselList)
      } catch (error) {
        console.error('Failed to fetch vessel list:', error)
        toast.error('Failed to load vessels')
      } finally {
        setIsLoadingVessels(false)
      }
    }

    fetchVessels()
  }, [currentUser, shipboardUser])

  const fetchCertificates = React.useCallback(async (signal: AbortSignal) => {
    if (!auth?.auth?.jwt || isLoadingVessels) return
    try {
      let vesselIds: string[]
      if (shipboardUser) {
        if (!selectedVesselId) {
          setCertificates([])
          toast.error('No vessel assigned')
          return
        }
        vesselIds = [selectedVesselId]
      } else {
        vesselIds = selectedVesselId
          ? vessels.filter(vessel => String(vessel.id) === selectedVesselId).map(vessel => String(vessel.id))
          : vessels.map(vessel => String(vessel.id))
      }

      setCertificates([])
      setCurrentPage(1)
      if (vesselIds.length === 0) return
      setLoading(true)
      const response = await fetch(`${OFFICE_CERTIFICATE_API_URL}/vessel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.auth.jwt}`,
        },
        body: JSON.stringify(vesselIds),
        signal,
      })
      if (!response.ok) throw new Error('Failed to fetch office certificates')
      const data = await response.json()
      if (!signal.aborted) setCertificates(data)
    } catch (error) {
      if (signal.aborted) return
      console.error(error)
      toast.error('Failed to load office certificates')
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }, [auth?.auth?.jwt, isLoadingVessels, shipboardUser, selectedVesselId, vessels])

  useEffect(() => {
    const controller = new AbortController()
    fetchCertificates(controller.signal)
    return () => controller.abort()
  }, [fetchCertificates, reloadTick, isUploadModalOpen])

  const handleSort = (key: SortConfig['key']) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const filteredAndSortedCertificates = React.useMemo(() => {
    let filtered = certificates.filter((cert) =>
      cert.certificateName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue: any = '';
        let bValue: any = '';

        switch (sortConfig.key) {
          case 'certificateName':
            aValue = a.certificateName.toLowerCase();
            bValue = b.certificateName.toLowerCase();
            break;
          case 'dateOfIssue':
            aValue = a.dateOfIssue ? new Date(a.dateOfIssue).getTime() : 0
            bValue = b.dateOfIssue ? new Date(b.dateOfIssue).getTime() : 0
            break;
          case 'dateOfExpiry':
            aValue = a.dateOfExpiry ? new Date(a.dateOfExpiry).getTime() : 0;
            bValue = b.dateOfExpiry ? new Date(b.dateOfExpiry).getTime() : 0;
            break;
          case 'uploadedByName':
            aValue = a.uploadedByName.toLowerCase();
            bValue = b.uploadedByName.toLowerCase();
            break;
          case 'remarks':
            aValue = (a.remarks || '').toLowerCase();
            bValue = (b.remarks || '').toLowerCase();
            break;
          case 'revisionCount':
            aValue = Number(a.revisionCount ?? 0)
            bValue = Number(b.revisionCount ?? 0)
            break
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [certificates, searchQuery, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedCertificates.length / rowsPerPage))
  const paginatedCertificates = filteredAndSortedCertificates.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  )

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value))
    setCurrentPage(1)
  }

  const expiryStats = certificates.reduce(
    (acc, cert) => {
      const s = getExpiryStatus(cert.dateOfExpiry).status
      if (s !== 'no-expiry') {
        acc[s as 'valid' | 'expiring' | 'expired']++
      }
      return acc
    },
    {valid: 0, expiring: 0, expired: 0}
  )

  const openEdit = (c: Certificate) => { setEditingCert(c); setIsEditOpen(true) }
  const closeEdit = () => { setIsEditOpen(false); setEditingCert(null) }

  const handleEditSubmit = async (p: {
    id: string | number, certificateName?: string, certificateNameId?: number, dateOfIssue?: string, dateOfExpiry?: string, remarks?: string, __file?: File | null
  }) => {
    try {
      await updateOfficeCertificate(Number(p.id), {
        id: p.id as any,
        certificateName: p.certificateName,
        certificateNameId: p.certificateNameId,
        dateOfIssue: p.dateOfIssue,
        dateOfExpiry: p.dateOfExpiry,
        remarks: p.remarks,
      })
      if (p.__file) {
        await replaceOfficeCertificateFile(Number(p.id), p.__file)
      }
      toast.success('Office certificate updated')
      forceReload() 
    } catch (e: any) {
      toast.error('Update failed')
    } finally {
      closeEdit()
    }
  }

  const handleDeleteCertificate = (certificateId: string) => {
    if (!canDeleteCert) {
      toast.error('You do not have permission to delete office certificates');
      return;
    }
    confirmDelete(`office-certificate-${certificateId}`, 'Are you sure you want to delete this office certificate?', async () => {
      try {
        const response = await fetch(`${OFFICE_CERTIFICATE_API_URL}/${certificateId}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.auth?.jwt}`,
          },
          method: 'DELETE',
        })

        if (!response.ok) {
          throw new Error('Failed to delete office certificate')
        }

        setCertificates((prev) => prev.filter((cert) => cert.id !== certificateId))
        toast.success('Office certificate deleted successfully')
      } catch (error) {
        console.error('Error deleting office certificate:', error)
        toast.error('Failed to delete office certificate. Please try again.')
      }
    })
  }

  const handleDownload = async (certificate: Certificate) => {
    if (!canDownloadCert) {
      toast.error('You do not have permission to download office certificates')
      return
    }
    try {
      setDownloading(certificate.id)
      const response = await fetch(`${OFFICE_CERTIFICATE_API_URL}/download/${certificate.id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth?.auth?.jwt}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${certificate.certificateName || 'office_certificate'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download error:', err)
      toast.error(
        'Failed to download document'
      )
    } finally {
      setDownloading(null)
    }
  }


  const viewerFile = selectedCertificate ? {
    name: selectedCertificate.certificateName,
    viewUrl: officeCertificateViewUrl(Number(selectedCertificate.id)),
    downloadUrl: officeCertificateDownloadUrl(Number(selectedCertificate.id)),
    uploadedDate: selectedCertificate.uploadedDate,
    fileSize: selectedCertificate.fileSize
  } : undefined;

  return (
    <div
      className='app-main flex-column flex-row-fluid p-5'
      id='kt_app_main'
      style={{height: '100vh', overflowX: 'hidden'}}
    >
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid' style={{flex: 1}}>
          <div className='container-fluid'>
            
            <div className='d-flex flex-wrap flex-stack mb-3'>
              <div className='d-flex flex-column'>
                <h1 className='fw-bold my-2'>Office Certificates</h1>
                <span className='fs-6 text-gray-400 fw-semibold'>
                  Manage office certification documents and expiry tracking
                </span>
              </div>
            </div>

            <div className='row g-5 g-xl-8 mb-6 px-5'>
              <div className='col-xl-3 p-0 me-3'>
                <div
                  className='card'
                  style={{
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    outline: 'none',
                    border: 'none',
                  }}
                >
                  <div className='card-body p-3'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-45px me-4'>
                        <div className='symbol-label bg-light-warning'>
                          <KTSVG
                            path='/media/icons/duotune/general/gen007.svg'
                            className='svg-icon-3 text-warning'
                          />
                        </div>
                      </div>
                      <div>
                        <span className='text-gray-800 fw-bold fs-2x'>{loading ? '-' : expiryStats.expiring}</span>
                        <div className='text-muted fw-semibold fs-7'>Expiring Soon</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-xl-3 p-0'>
                <div
                  className='card'
                  style={{
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    outline: 'none',
                    border: 'none',
                  }}
                >
                  <div className='card-body p-3'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-45px me-4'>
                        <div className='symbol-label bg-light-danger'>
                          <KTSVG
                            path='/media/icons/duotune/general/gen024.svg'
                            className='svg-icon-3 text-danger'
                          />
                        </div>
                      </div>
                      <div>
                        <span className='text-gray-800 fw-bold fs-2x'>{isDataLoading ? '-' : expiryStats.expired}</span>
                        <div className='text-muted fw-semibold fs-7'>Expired</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className='card py-4'
              style={{backgroundColor: '#fff', boxShadow: 'none', outline: 'none'}}
            >
              <div className='card-header border-0 d-flex flex-column gap-3'>
                <span className='card-label fw-bold fs-3 mb-1'>Office Certificates</span>

                <span className='text-muted mt-1 fw-semibold fs-7'>
                  {selectedVesselId
                    ? `Office Certificates for ${
                        vessels.find((v) => v.id.toString() === selectedVesselId)?.fleet_name ||
                        'Selected Vessel'
                      }`
                    : globalState.company
                    ? `Certificates for ${globalState.company.name}`
                    : 'All Office Certificates'}
                </span>
                
                <div
                  className='d-flex align-items-center position-relative flex-grow-1 me-3 gap-3'
                  style={{flexWrap: 'wrap'}}
                >
                  <div className='d-flex align-items-center position-relative flex-grow-1 me-3 mt-3' style={{minWidth: '200px'}}>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      placeholder='Search certificates...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  {showCertificateLibrary && (
                    <button
                      className='btn btn-light-primary mt-3'
                      onClick={() => setIsLibraryOpen(true)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen048.svg'
                        className='svg-icon-2 me-2'
                      />
                      Certificate Library
                    </button>
                  )}
                  {canCreateCert && (selectedVesselId || shipboardUser) && (
                    <button className='btn btn-primary mt-3' onClick={() => setIsUploadModalOpen(true)}>
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr075.svg'
                        className='svg-icon-2 me-2'
                      />
                      Add Office Certificate
                    </button>
                  )}
                </div>
              </div>

              <div className='card-body pt-0'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='text-center' style={{ width: '70px' }}>SR/NO</th>
                        <th 
                          onClick={() => handleSort('certificateName')} 
                          className='min-w-250px cursor-pointer'
                        >
                          Certificate Name
                        </th>
                        <th 
                          onClick={() => handleSort('dateOfIssue')} 
                          className='min-w-120px cursor-pointer'
                        >
                          Date of Issue
                        </th>
                        <th 
                          onClick={() => handleSort('dateOfExpiry')} 
                          className='min-w-150px cursor-pointer'
                        >
                          Date of Expiry
                        </th>
                        <th 
                          onClick={() => handleSort('revisionCount')} 
                          className='min-w-110px cursor-pointer text-nowrap'
                        >
                          Revision No
                        </th>
                        <th 
                          onClick={() => handleSort('uploadedByName')} 
                          className='min-w-120px cursor-pointer'
                        >
                          Uploaded By
                        </th>
                        <th className='min-w-100px'>File</th>
                        <th 
                          onClick={() => handleSort('remarks')} 
                          className='min-w-150px cursor-pointer'
                          style={{minWidth: '160px', maxWidth: '240px'}}
                        >
                          Remarks
                        </th>
                        <th className='min-w-100px text-end'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isDataLoading ? (
                        <TableSkeleton rows={8} cols={9} />
                      ) : paginatedCertificates.length === 0 ? (
                        <tr>
                          <td colSpan={9} className='text-center py-10'>
                            <div className='d-flex flex-column align-items-center'>
                              <span className='text-muted fs-6'>
                                {searchQuery
                                  ? 'No certificates found matching your search.'
                                  : 'No office certificates uploaded yet.'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedCertificates.map((certificate, index) => {
                          const expiryInfo = getExpiryStatus(certificate.dateOfExpiry)
                          const serialNumber = (currentPage - 1) * rowsPerPage + index + 1

                          return (
                            <tr key={certificate.id}>
                              <td className='text-center'>
                                <span className='text-muted fw-semibold fs-7'>
                                  {serialNumber}
                                </span>
                              </td>
                              <td>
                                <div className='d-flex align-items-center ms-5'>
                                  <KTSVG
                                    path='/media/icons/duotune/files/fil003.svg'
                                    className='svg-icon-2 text-primary me-4'
                                  />
                                  <div className='d-flex flex-column'>
                                    <span
                                      className='text-gray-800 fw-bold text-hover-primary fs-6 cursor-pointer'
                                      onClick={() => setSelectedCertificate(certificate)}
                                    >
                                      {certificate.certificateName} ({certificate.fileSize})
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className='text-muted fw-semibold fs-7'>
                                  {formatDate(certificate.dateOfIssue)}
                                </span>
                              </td>
                              <td>
                                {expiryInfo.status === 'no-expiry' ? (
                                  <span className='text-muted fw-semibold fs-7'>No Expiry</span>
                                ) : (
                                  <div className='d-flex flex-column'>
                                    <div className='d-flex align-items-center'>
                                      <span className={`fw-bold fs-6 text-${expiryInfo.color} me-2`}>
                                        {formatDate(certificate.dateOfExpiry)}
                                      </span>
                                      {expiryInfo.status !== 'valid' && (
                                        <span className={`badge badge-light-${expiryInfo.color} badge-sm`} style={{fontSize: '10px', padding: '2px 6px'}}>
                                          {expiryInfo.status === 'expired'
                                            ? 'EXPIRED'
                                            : `${expiryInfo.days} days left`}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className='text-muted fw-semibold fs-7 text-nowrap'>
                                <span 
                                  className="text-primary cursor-pointer text-decoration-underline"
                                  onClick={() => setRevModal({id: Number(certificate.id), name: certificate.certificateName, visible: true})}
                                >
                                  {certificate.revisionCount ?? 0}
                                </span>
                              </td>

                              <td>
                                <span className='text-muted fw-semibold fs-7'>
                                  {certificate.uploadedByName}
                                </span>
                              </td>
                              <td>
                                <button
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() => handleDownload(certificate)}
                                  title='Download PDF'
                                  disabled={downloading === certificate.id || !canDownloadCert}
                                >
                                  {downloading === certificate.id ? (
                                    <>
                                      <span className='spinner-border spinner-border-sm me-2'></span>
                                      Downloading...
                                    </>
                                  ) : (
                                    'Download'
                                  )}
                                </button>
                              </td>
                              <td>
                                <RemarksCell
                                  text={certificate.remarks}
                                  title="Office Certificate Remarks"
                                  subtitle={certificate.certificateName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td className='text-end text-nowrap'>
                                {canUpdateCert && (
                                  <button
                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                    onClick={() => openEdit(certificate)}
                                    title='Edit'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/art/art005.svg'
                                      className='svg-icon-3'
                                    />
                                  </button>
                                )}
                                {canDeleteCert && (
                                  <button
                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                    onClick={() => handleDeleteCertificate(certificate.id)}
                                    title='Delete'
                                  >
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen027.svg'
                                      className='svg-icon-3'
                                    />
                                  </button>
                                )}
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && !isDataLoading && (
                  <div className='d-flex justify-content-between align-items-center flex-wrap pt-10'>
                    <div className='d-flex align-items-center py-3'>
                      <span className='text-muted me-2'>Rows per page:</span>
                      <SearchableSelect
                        className='form-select form-select-sm form-select-solid w-75px'
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </SearchableSelect>
                    </div>

                    <div className='d-flex align-items-center py-3'>
                      <span className='text-muted me-3'>
                        Showing {((currentPage - 1) * rowsPerPage) + 1} to{' '}
                        {Math.min(currentPage * rowsPerPage, filteredAndSortedCertificates.length)} of{' '}
                        {filteredAndSortedCertificates.length} entries
                      </span>

                      <ul className='pagination pagination-outline mb-0'>
                        <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            <i className='previous'></i>
                          </button>
                        </li>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button className='page-link' onClick={() => handlePageChange(page)}>
                              {page}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            <i className='next'></i>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <OfficeCertificateUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={() => {}}
        selectedVesselId={selectedVesselId}
      />

      {editingCert && (
        <OfficeCertificateEditModal
          visible={isEditOpen}
          onClose={closeEdit}
          record={editingCert as any}
          onSubmit={handleEditSubmit}
          onViewFile={() => setSelectedCertificate(editingCert)}
          vesselName={allVessels.find(v => String(v.id) === String(editingCert.vesselId ?? selectedVesselId))?.fleet_name}
        />
      )}

      {selectedCertificate && viewerFile && (
        <CertificateViewer
          file={viewerFile}
          onClose={() => setSelectedCertificate(null)}
        />
      )}

      <OfficeCertificateRevisionsModal
        certificateId={revModal.id}
        name={revModal.name}
        visible={revModal.visible}
        onClose={() => {
          setRevModal({id:0, name:'', visible:false})
          forceReload()
        }}
      />

      <CertificateLibraryModal
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        targetType='OFFICE'
      />

      <RemarksModal
        visible={remarksModal.visible}
        title={remarksModal.title}
        subtitle={remarksModal.subtitle}
        remarksText={remarksModal.text}
        onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
      />

      <ToastContainer />
    </div>
  )
}
